import { Label, Link, Select } from '@fluentui/react-components'
import { pages } from '@microsoft/teams-js'
import { FC, MouseEventHandler, ReactEventHandler, useEffect, useState } from 'react'
import styles from './ConfigurableTab.module.css'
import { useTranslation } from 'react-i18next'

interface ConfigurableTabDetailsProps {
  defaultDomain: string | null
  authToken: string
}

interface ViewPreference {
  internal_id: number
  view_pref_name: string
  workspace_name: string
  tab_name: string
  url: string
}

export const ConfigurableTabDetails: FC<ConfigurableTabDetailsProps> = ({ defaultDomain, authToken }) => {
  const [token] = useState<string>(authToken)
  const [options, setOptions] = useState<ViewPreference[]>([])
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>('')
  const [selectedView, setSelectedView] = useState<number>(0)
  const [displayUrl, setDisplayUrl] = useState<string>('')
  const { t } = useTranslation()

  const workspaces: string[] = options.map((view) => view.workspace_name).filter((value, index, array) => array.indexOf(value) === index)
  const boardTabName = 'Board'
  const hasBoards = options.find(x => x.tab_name === boardTabName) !== undefined

  // Call the API to get the View Preferences
  useEffect(() => {
    if (defaultDomain !== null) {
      fetch(`https://${defaultDomain}/sciforma/rest/view-preferences?offset=0&limit=1000`, { credentials: 'include' })
        .then(async (res) => await res.json())
        .then((data: ViewPreference[]) => {
          if (data !== undefined && data.length > 0) {
            setOptions(data)
            setSelectedValues()
          }
        })
        .catch((err) => {
          console.log(err.message)
        })
    }
  }, [defaultDomain, token])

  // Call the validate when options have been selected
  useEffect(() => {
    if (displayUrl !== '') {
      pages.config.setValidityState(true)

      const websiteUrl = `${window.location.origin}/display-tab`
      const contentUrl = `${window.location.origin}/display-tab?Url=${btoa(displayUrl)}`
      const entityId = `${randomNumber(3)}-${selectedWorkspace}-${selectedView}`
      const viewName = options.find(x => x.internal_id === selectedView)?.view_pref_name ?? ''
      const suggestedDisplayName = `${viewName}`

      pages.config.registerOnSaveHandler((saveEvent) => {
        const configPromise = pages.config.setConfig({
          websiteUrl,
          contentUrl,
          entityId,
          suggestedDisplayName
        })
        configPromise
          .then(() => { saveEvent.notifySuccess() })
          .catch(() => { saveEvent.notifyFailure('failure message') })
      })
    }
  }, [displayUrl, options, selectedView, selectedWorkspace])

  useEffect(() => {
    setSelectedValues()
  }, [])

  const setSelectedValues = (): void => {
    pages.getConfig()
      .then((instanceConfig) => {
        const entityParts = instanceConfig.entityId?.split('-')
        if (entityParts !== undefined) {
          setSelectedWorkspace(entityParts[1])
          setSelectedView(parseInt(entityParts[2]))
        }
      })
      .catch((reason) => console.log(reason))
  }

  const workspaceChanged: ReactEventHandler<HTMLSelectElement> = (e) => {
    setSelectedWorkspace(e.currentTarget.value)
    
    // The workspace has been changed - so set the view to the first one in the list for this workspace
    const viewPreference = options.find(x => x.workspace_name === e.currentTarget.value)
    if(viewPreference !== undefined){
      setView((viewPreference as ViewPreference).internal_id)
    
    }
  }

  const viewChanged: ReactEventHandler<HTMLSelectElement> = (e) => {
    setView(parseInt(e.currentTarget.value))
  }

  const viewClicked: MouseEventHandler<HTMLOptionElement> = (e) => {
    setView(parseInt(e.currentTarget.value))
  }

  const setView = (internalId: number): void => {
    const viewPreference = options.find(x => x.internal_id === internalId)
    setDisplayUrl(viewPreference?.url ?? '')
    setSelectedView(internalId)
  }

  const randomNumber =
    (length: number): number => Math.floor(Math.random() * (9 * Math.pow(10, length - 1))) + Math.pow(10, length - 1)

  return (
    <div>
      <div>
        <Label>{t('SELECT_WORKSPACE')}:</Label>
        <br />
        <Select onChange={workspaceChanged}>
          <option selected={selectedWorkspace === ''} />
          {workspaces.map((workspace, i) => (<option value={workspace} selected={workspace === selectedWorkspace} key={i}>{workspace}</option>))}
        </Select>
      </div>
      <div>
        <Label>{t('SELECT_VIEW')}:</Label>
        {hasBoards
          ? <Select onChange={viewChanged}>
            <optgroup label='Boards'>
              {options.filter((option) => option.workspace_name === selectedWorkspace && option.tab_name === boardTabName).map((workspace, i) => (<option onClick={viewClicked} value={workspace.internal_id} selected={workspace.internal_id === selectedView} key={i}>{workspace.view_pref_name}</option>))}
            </optgroup>
            <optgroup label='Grids'>
              {options.filter((option) => option.workspace_name === selectedWorkspace && option.tab_name !== boardTabName).map((workspace, i) => (<option onClick={viewClicked} value={workspace.internal_id} selected={workspace.internal_id === selectedView} key={i}>{workspace.view_pref_name}</option>))}
            </optgroup>
            </Select>
          : <Select onChange={viewChanged}>
            {options.filter((option) => option.workspace_name === selectedWorkspace).map((workspace, i) => (<option onClick={viewClicked} value={workspace.internal_id} selected={workspace.internal_id === selectedView} key={i}>{workspace.view_pref_name}</option>))}
            </Select>}
      </div>
    </div>
  )
}
